import React, { useState, useEffect, useContext } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { UserPlus, LogIn, RefreshCw } from 'lucide-react';

export const AuthPrompts = () => {
  const { user, sessionExpired, clearSessionExpired } = useContext(AuthContext);
  const location = useLocation();
  const navigate = useNavigate();
  const [guestOpen, setGuestOpen] = useState(false);

  const onAuthPage = location.pathname.startsWith('/auth');

  useEffect(() => {
    if (user || sessionExpired || onAuthPage) return;
    if (sessionStorage.getItem('guestPromptShown')) return;
    const t = setTimeout(() => setGuestOpen(true), 4000);
    return () => clearTimeout(t);
  }, [user, sessionExpired, onAuthPage]);

  const dismissGuest = () => {
    sessionStorage.setItem('guestPromptShown', '1');
    setGuestOpen(false);
  };

  const goAuth = (dismiss) => {
    if (dismiss) dismissGuest();
    else clearSessionExpired();
    navigate('/auth');
  };

  return (
    <>
      <Dialog open={guestOpen && !user && !onAuthPage} onOpenChange={(o) => !o && dismissGuest()}>
        <DialogContent className="sm:max-w-md text-center" dir="rtl" data-testid="guest-register-prompt">
          <DialogHeader>
            <DialogTitle className="text-2xl text-center">🎾 הצטרפו לקהילה!</DialogTitle>
            <DialogDescription className="text-center text-base">
              כדי להירשם לתחרויות, לשלוח הודעות, למכור ולקנות ציוד — חייבים לפתוח משתמש. ההרשמה חינם ולוקחת פחות מדקה!
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <Button onClick={() => goAuth(true)} className="w-full bg-gradient-to-l from-blue-600 to-green-600 hover:from-blue-700 hover:to-green-700 rounded-full py-6 text-lg font-bold" data-testid="guest-register-btn">
              <UserPlus className="w-5 h-5 ml-2" />
              הירשמו עכשיו בחינם
            </Button>
            <Button variant="outline" onClick={() => goAuth(true)} className="w-full rounded-full" data-testid="guest-login-btn">
              <LogIn className="w-4 h-4 ml-2" />
              כבר יש לי משתמש — התחברות
            </Button>
            <button onClick={dismissGuest} className="text-sm text-gray-400 hover:text-gray-600 underline" data-testid="guest-dismiss-btn">
              אולי מאוחר יותר
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!sessionExpired} onOpenChange={(o) => !o && clearSessionExpired()}>
        <DialogContent className="sm:max-w-md text-center" dir="rtl" data-testid="session-expired-prompt">
          <DialogHeader>
            <DialogTitle className="text-2xl text-center">⏳ החיבור שלך פג</DialogTitle>
            <DialogDescription className="text-center text-base">
              התנתקת מהאתר. יש להתחבר מחדש כדי להמשיך להשתמש בכל האפשרויות.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <Button onClick={() => goAuth(false)} className="w-full bg-gradient-to-l from-blue-600 to-green-600 hover:from-blue-700 hover:to-green-700 rounded-full py-6 text-lg font-bold" data-testid="session-relogin-btn">
              <RefreshCw className="w-5 h-5 ml-2" />
              התחבר מחדש
            </Button>
            <button onClick={clearSessionExpired} className="text-sm text-gray-400 hover:text-gray-600 underline" data-testid="session-dismiss-btn">
              המשך כאורח
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
